import React from 'react';
import Footer from './Footer';
import SEO from './SEO';
import './FractoStudio.css';

const EDITOR_URL = 'https://onlinefreevideoeditor.com/edit';
const DEMO_VIDEO_URL = 'https://websitestorage.blr1.cdn.digitaloceanspaces.com/editor-fracto.mp4';
const DEMO_VIDEO_V1_URL = 'https://websitestorage.blr1.cdn.digitaloceanspaces.com/demo_v1.mp4';

const demoVideos = [
  {
    title: 'Product demo',
    description: 'See how creators and podcasters edit, export, and publish with FractoStudio.',
    src: DEMO_VIDEO_V1_URL
  }
];

const tools = [
  { name: 'Video editor', desc: 'Full timeline editing in your browser' },
  { name: 'Add subtitles', desc: 'Captions and titles that move on screen' },
  { name: 'Cut & crop', desc: 'Trim clips and reframe for any platform' },
  { name: 'Merge & speed', desc: 'Combine clips and control playback pace' },
  { name: 'Resize', desc: '9:16, 16:9, and social presets built in' },
  { name: 'Compress', desc: 'Export lighter files without losing clarity' },
  { name: 'Meme maker', desc: 'Quick meme templates and effects' },
  { name: 'Filters', desc: 'Style footage with transitions and looks' }
];

const podcastFeatures = [
  {
    title: 'Sync multi-cam by audio',
    text: 'Auto-align podcast angles — same workflow as Premiere multi-cam sync.'
  },
  {
    title: 'Picture cuts & Split layout',
    text: 'Press 1 / 2 to switch cameras at the playhead; S for side-by-side like CapCut split screen.'
  },
  {
    title: 'Independent voice (mic1 / mic2)',
    text: 'Shift+1 / Shift+2 choose which mic plays — picture and audio stay separate, like Final Cut Pro.'
  }
];

const creatorFeatures = [
  {
    title: '9:16 & 16:9 canvas presets',
    text: 'TikTok, Reels, Shorts, YouTube, and Instagram formats built in.'
  },
  {
    title: 'Text, captions & motion',
    text: 'One-click title styles and animated overlays that move on screen.'
  },
  {
    title: 'Filters & stock transitions',
    text: 'Style your footage and drop transition clips between scenes.'
  }
];

const steps = [
  { num: '01', title: 'Open FractoStudio', text: 'Go to the editor in your browser — no sign-up or install.' },
  { num: '02', title: 'Import & edit', text: 'Add media, cut on the timeline, and apply effects.' },
  { num: '03', title: 'Export your video', text: 'Download a clean export with no watermark.' }
];

const stats = [
  { value: '100%', label: 'Free forever' },
  { value: '$0', label: 'No subscription' },
  { value: 'Local', label: 'Your files stay yours' },
  { value: '0', label: 'Watermarks' }
];

const FractoStudio = () => {
  const openEditor = () => window.open(EDITOR_URL, '_blank', 'noopener,noreferrer');

  return (
    <>
      <SEO
        title="FractoStudio — Free Online Video Editor | Podcast, Multi-Cam & Creators"
        description="FractoStudio is Fractallogy's free online video editor for podcasters and creators. Edit in your browser with multi-cam sync, captions, Shorts and Reels presets, and watermark-free exports. No sign-up required."
        keywords="FractoStudio, online video editor, free video editor, browser video editor, podcast video editor, multi-cam video editor, TikTok editor, YouTube Shorts editor, Reels editor, video editor no watermark, local video editing, Fractallogy video editor"
        url="/online-video-editor"
        image="https://fractallogy.com/images/fracto-video-logo.png"
        imageAlt="FractoStudio — free online video editor by Fractallogy"
        type="website"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Online Video Editor', url: '/online-video-editor' }
        ]}
        faq={[
          {
            question: 'Is FractoStudio really free?',
            answer: 'Yes. FractoStudio is free to use with no subscription, no watermark on exports, and no forced sign-up to start editing.'
          },
          {
            question: 'Does FractoStudio work in the browser?',
            answer: 'FractoStudio runs entirely in your web browser. Open the editor, import media, edit on the timeline, and export without installing desktop software.'
          },
          {
            question: 'Can FractoStudio sync multi-camera podcast footage?',
            answer: 'Yes. FractoStudio can sync multi-camera podcast clips by audio, switch camera angles during editing, and handle independent microphone tracks.'
          },
          {
            question: 'What video formats does FractoStudio support?',
            answer: 'FractoStudio includes presets for vertical and horizontal formats such as TikTok, Reels, Shorts, YouTube, and Instagram, plus tools for cut, crop, merge, speed, subtitles, and compression.'
          }
        ]}
        product={{
          name: 'FractoStudio',
          description: 'Free online video editor for podcasters and creators with multi-cam sync, social presets, and local-first editing in the browser.',
          operatingSystem: 'Web Browser',
          applicationCategory: 'MultimediaApplication',
          downloadUrl: EDITOR_URL,
          screenshot: 'https://fractallogy.com/images/fracto-video-logo.png',
          price: '0',
          priceCurrency: 'USD',
          features: 'Multi-cam audio sync, Subtitles, Cut, Crop, Merge, Speed, Resize, Compress, Meme maker, Filters, 9:16 and 16:9 presets, No watermark, Browser-based editing'
        }}
      />

      <section className="fracto-studio-page">
        <div className="fracto-studio-hero">
          <div className="fracto-studio-hero-glow" aria-hidden="true" />
          <div className="fracto-studio-container fracto-studio-hero-layout">
            <div className="fracto-studio-hero-inner">
              <span className="fracto-studio-kicker">Fractallogy Product</span>
              <h1>
                Edit smarter.
                <span> Export freely.</span>
              </h1>
              <p className="fracto-studio-hero-lead">
                Try FractoStudio&apos;s free online video editor — in your browser, on your device.
                Professional workflows for podcasters, creators, and teams who want privacy without paywalls.
              </p>
              <div className="fracto-studio-hero-actions">
                <button type="button" className="fracto-studio-btn fracto-studio-btn-primary" onClick={openEditor}>
                  Start editing free
                </button>
                <a href={EDITOR_URL} target="_blank" rel="noopener noreferrer" className="fracto-studio-btn fracto-studio-btn-ghost">
                  Open FractoStudio
                </a>
              </div>
              <div className="fracto-studio-hero-badges">
                <span>No sign-up</span>
                <span>No watermark</span>
                <span>Local-first</span>
              </div>
            </div>
            <div className="fracto-studio-hero-visual">
              <video
                className="fracto-studio-hero-video"
                src={DEMO_VIDEO_URL}
                poster="/images/fracto-video-logo.png"
                autoPlay
                loop
                muted
                playsInline
                aria-label="FractoStudio editor demo"
              />
            </div>
          </div>
        </div>

        <div className="fracto-studio-section fracto-studio-demos">
          <div className="fracto-studio-container">
            <div className="fracto-studio-section-head">
              <span className="fracto-studio-label">See it work</span>
              <h2>Watch FractoStudio in action</h2>
              <p>
                From timeline editing to export-ready output — see what you can build without installing anything.
              </p>
            </div>
            <div className="fracto-studio-demos-grid">
              {demoVideos.map((demo) => (
                <article className="fracto-studio-demo-card" key={demo.src}>
                  <div className="fracto-studio-demo-video-wrap">
                    <video
                      className="fracto-studio-demo-video"
                      src={demo.src}
                      poster="/images/fracto-video-logo.png"
                      autoPlay
                      loop
                      muted
                      playsInline
                      aria-label={demo.title}
                    />
                  </div>
                  <h3>{demo.title}</h3>
                  <p>{demo.description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>

        <div className="fracto-studio-section fracto-studio-tools">
          <div className="fracto-studio-container">
            <div className="fracto-studio-section-head">
              <span className="fracto-studio-label">Video editor &amp; tools</span>
              <h2>Simplify video creation and unlock your creativity</h2>
              <p>
                You can easily make impressive and captivating videos using FractoStudio.
                With built-in editing features, creating videos only requires a few simple steps.
              </p>
            </div>
            <div className="fracto-studio-tools-grid">
              {tools.map((tool) => (
                <article className="fracto-studio-tool-card" key={tool.name}>
                  <h3>{tool.name}</h3>
                  <p>{tool.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </div>

        <div className="fracto-studio-section fracto-studio-audience">
          <div className="fracto-studio-container">
            <div className="fracto-studio-section-head">
              <span className="fracto-studio-label">Who it&apos;s for</span>
              <h2>Built for podcasters &amp; creators</h2>
              <p>
                FractoStudio isn&apos;t just a trim-and-export tool — it handles real workflows
                for interviews, vlogs, and social video.
              </p>
            </div>

            <div className="fracto-studio-audience-grid">
              <article className="fracto-studio-audience-card fracto-studio-audience-card--podcast">
                <span className="fracto-studio-audience-tag">For podcasters</span>
                <h3>Multi-camera podcasts, synced by voice</h3>
                <p className="fracto-studio-audience-intro">
                  Record the same episode from two angles? FractoStudio syncs clips by audio like Premiere Pro
                  or DaVinci Resolve, then lets you cut between cameras with 1 / 2 — or show both side-by-side
                  with Split — while mic1 / mic2 picks the voice track.
                </p>
                <ul>
                  {podcastFeatures.map((item) => (
                    <li key={item.title}>
                      <strong>{item.title}</strong>
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </article>

              <article className="fracto-studio-audience-card fracto-studio-audience-card--creator">
                <span className="fracto-studio-audience-tag">For content creators</span>
                <h3>Shorts, Reels &amp; tutorials — all in the browser</h3>
                <p className="fracto-studio-audience-intro">
                  Whether you film vertical TikToks or widescreen YouTube videos, FractoStudio adapts.
                  Add captions, filters, and transitions, then export in the format your platform expects.
                </p>
                <ul>
                  {creatorFeatures.map((item) => (
                    <li key={item.title}>
                      <strong>{item.title}</strong>
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </div>
          </div>
        </div>

        <div className="fracto-studio-section fracto-studio-steps-section">
          <div className="fracto-studio-container">
            <div className="fracto-studio-section-head fracto-studio-section-head--light">
              <span className="fracto-studio-label">How it works</span>
              <h2>Three steps to your finished video</h2>
            </div>
            <div className="fracto-studio-steps">
              {steps.map((step) => (
                <article className="fracto-studio-step" key={step.num}>
                  <span className="fracto-studio-step-num">{step.num}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>

        <div className="fracto-studio-section fracto-studio-why">
          <div className="fracto-studio-container">
            <div className="fracto-studio-why-inner">
              <div className="fracto-studio-why-copy">
                <span className="fracto-studio-label">Why FractoStudio</span>
                <h2>Professional editing, zero catch</h2>
                <p>
                  Most &ldquo;free&rdquo; editors add watermarks, force sign-ups, or upload your footage to the cloud.
                  FractoStudio stays free, private, and in your browser.
                </p>
                <button type="button" className="fracto-studio-btn fracto-studio-btn-primary" onClick={openEditor}>
                  Open the editor
                </button>
              </div>
              <div className="fracto-studio-stats">
                {stats.map((stat) => (
                  <div className="fracto-studio-stat" key={stat.label}>
                    <strong>{stat.value}</strong>
                    <span>{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="fracto-studio-cta">
          <div className="fracto-studio-container fracto-studio-cta-inner">
            <h2>Ready to edit your next video?</h2>
            <p>Open FractoStudio in your browser — no download required.</p>
            <button type="button" className="fracto-studio-btn fracto-studio-btn-primary fracto-studio-btn-large" onClick={openEditor}>
              Start editing free
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default FractoStudio;
